<template>
  <WizardStep
    v-if="hasEditMerchantSettingsPermission"
    @submit="submit"
    @reset="reset"
  >
    <template v-slot:content :class="FAC - container">
      <div class="row ">
        <div class="col-xl-6 ">
          <div class="form-group FAC-setting-container">
            <label>FAC ID</label>
            <b-form-input
              type="text"
              class="form-control"
              v-model.trim="$v.fac.facId.$model"
              :state="validateState('facId')"
              placeholder="FAC ID"
            />
            <div v-if="!$v.fac.facId.required">
              FAC ID is required
            </div>
          </div>

          <div class="form-group FAC-setting-container">
            <label>FAC Password</label>
            <div class="d-flex align-items-center">
              <b-form-input
                type="password"
                class="form-control"
                v-model.trim="$v.fac.processingPassword.$model"
                :state="validateState('processingPassword')"
                placeholder="FAC Password"
                :disabled="!enablePasswordField"
              />

              <b-checkbox
                v-if="updateCredentials"
                style="padding-left: 2.875rem;"
                v-model="enablePasswordField"
                size="lg"
              ></b-checkbox>
            </div>
            <div v-if="!$v.fac.processingPassword.required">
              FAC Password is required
            </div>
          </div>

          <div class="form-group FAC-setting-container">
            <label>Powertranz ID</label>
            <b-form-input
              type="text"
              class="form-control"
              v-model.trim="$v.fac.powertransId.$model"
              :state="validateState('powertransId')"
              placeholder="Powertranz ID"
            />
            <div v-if="!$v.fac.powertransId.required">
              Powertranz ID is required
            </div>
          </div>

          <div class="form-group FAC-setting-container">
            <label>Powertranz Password</label>
            <div class="d-flex align-items-center">
              <b-form-input
                type="password"
                class="form-control"
                v-model.trim="$v.fac.powertranzPassword.$model"
                :state="validateState('powertranzPassword')"
                placeholder="Powertranz Password"
                :disabled="!enablePowertranzPasswordField"
              />

              <b-checkbox
                v-if="updateCredentials"
                style="padding-left: 2.875rem;"
                v-model="enablePowertranzPasswordField"
                size="lg"
              ></b-checkbox>
            </div>
            <div v-if="!$v.fac.powertranzPassword.required">
              Powertranz Password is required
            </div>
          </div>

          <div class="form-group FAC-setting-container">
            <label>Acquirer ID</label>
            <b-form-input
              type="text"
              class="form-control"
              v-model.trim="$v.fac.acquirerId.$model"
              :state="validateState('acquirerId')"
              placeholder="Acquirer ID"
            />
            <div v-if="!$v.fac.acquirerId.required">
              Acquirer ID is required
            </div>
          </div>

          <div class="form-group FAC-setting-container">
            <label id="enableTwoPassLabel">Enable Two Pass Transactions</label>
            <b-form-checkbox
              switch
              size="lg"
              v-model="$v.fac.twoPass.$model"
              :state="validateState('twoPass')"
            />
            <b-tooltip target="enableTwoPassLabel" triggers="hover">
              When disabled, transactions will be authorized and captured
              automatically in one step. When enabled, transactions will be
              authorized. They will have to be captured manually in the
              transactions table.
            </b-tooltip>
            <div v-if="!$v.fac.twoPass.required">
              This field is required
            </div>
          </div>

          <div class="form-group FAC-setting-container">
            <label id="includeOrderNumberLabel"
              >Include Payment Order Number in the Order Id</label
            >
            <b-form-checkbox
              switch
              size="lg"
              v-model="$v.fac.includeOrderNumber.$model"
              :state="validateState('includeOrderNumber')"
            />
            <b-tooltip target="includeOrderNumberLabel" triggers="hover">
              When enabled, the generated FAC Order ID will include the
              transaction order number
            </b-tooltip>
            <div v-if="!$v.fac.includeOrderNumber.required">
              This field is required
            </div>
          </div>
        </div>
      </div>
    </template>
  </WizardStep>
</template>

<script>
import WizardStep from "@/components/wizard/WizardStep";
import { required, minLength, requiredIf } from "vuelidate/lib/validators";
import MerchantSettingsPermissionsMixin from "@/components/merchantsettings/MerchantSettingsPermissionsMixin.js";
import { API, graphqlOperation } from "aws-amplify";

export default {
  mixins: [MerchantSettingsPermissionsMixin],
  components: { WizardStep },
  props: ["heading"],
  data: () => {
    return {
      fac: {
        processingPassword: null,
        powertranzPassword: null,
        facId: null,
        powertransId: null,
        acquirerId: null,
        twoPass: false,
        includeOrderNumber: true
      },
      updateCredentials: false,
      changePassword: false,
      changePowertranzPassword: false
    };
  },
  validations: {
    fac: {
      facId: {
        required,
        minLength: minLength(6)
      },
      powertransId: {
        required,
        minLength: minLength(6)
      },
      processingPassword: {
        required: requiredIf(function() {
          return this.enablePasswordField;
        })
      },
      powertranzPassword: {
        required: requiredIf(function() {
          return this.enablePowertranzPasswordField;
        })
      },
      acquirerId: {
        required
      },
      twoPass: {
        required
      },
      includeOrderNumber: {
        required
      }
    }
  },
  computed: {
    enablePasswordField: {
      get() {
        return !this.updateCredentials || this.changePassword;
      },
      set(val) {
        this.changePassword = val;
      }
    },
    enablePowertranzPasswordField: {
      get() {
        return !this.updateCredentials || this.changePowertranzPassword;
      },
      set(val) {
        this.changePowertranzPassword = val;
      }
    }
  },
  methods: {
    async saveCredentials() {
      const mutationName = this.updateCredentials
        ? "updateFacCredentials"
        : "saveFacCredentials";
      if (!this.enablePasswordField) {
        this.fac.processingPassword = undefined;
      }
      if (!this.enablePowertranzPasswordField) {
        this.fac.powertranzPassword = undefined;
      }
      const result = await this.mutation(mutationName, {
        body: this.fac
      });
      return result.data[mutationName];
    },
    async submit() {
      if (!this.hasEditMerchantSettingsPermission) return;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.submitStatus = "ERROR";
      } else {
        // do your submit logic here
        this.submitStatus = "PENDING";
        var result = await this.saveCredentials();
        if (result)
          this.$bvToast.toast("Saved Successfully", {
            title: "FAC Settings",
            autoHideDelay: 5000,
            variant: "success",
            append: true
          });
        else {
          this.$bvToast.toast("An error occurred while saving", {
            title: "FAC Settings",
            autoHideDelay: 5000,
            variant: "danger",
            append: true
          });
        }
        this.submitStatus = "OK";
      }
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.fac[name];
      return $dirty ? !$error : null;
    },

    async populateFields() {
      const getCredentials = `
      query GetFACCredentials {
        getFACCredentials {
          facId
          powertransId
          acquirerId
          twoPass
          includeOrderNumber
        }
      }`;

      const facCredentials = await API.graphql(
        graphqlOperation(getCredentials)
      );

      this.updateCredentials = !!facCredentials.data.getFACCredentials.facId;

      this.fac.facId = facCredentials.data.getFACCredentials.facId;
      this.fac.powertransId =
        facCredentials.data.getFACCredentials.powertransId;
      this.fac.acquirerId = facCredentials.data.getFACCredentials.acquirerId;
      this.fac.twoPass = !!facCredentials.data.getFACCredentials.twoPass;
      this.fac.includeOrderNumber = !!facCredentials.data.getFACCredentials
        .includeOrderNumber;
    },

    async reset() {
      await this.populateFields();
      this.fac.processingPassword = null;
      this.changePassword = false;
      this.fac.powertranzPassword = null;
      this.changepowertranzPassword = false;
      this.$bvToast.toast("Cancelled Successfully", {
        title: "FAC Settings",
        autoHideDelay: 5000,
        variant: "success",
        append: true
      });
    }
  },
  async mounted() {
    await this.populateFields();
  }
};
</script>

<style lang="scss" scoped>
.FAC-setting-container > label {
  clear: both;
  float: left;
  font-weight: 500 !important;
  font-size: 14px !important;
}
.FAC-setting-container {
  display: grid !important;
  grid-template-columns: 2fr 2fr !important;
}

@media only screen and (max-width: 1024px) {
  .FAC-setting-container > label {
    clear: none;
    float: none;
    font-weight: 500 !important;
    font-size: 14px !important;
  }
  .FAC-setting-container {
    display: flex !important;
    flex-direction: column;
  }
}
</style>
